import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { useDemoStore, roleHomes } from "@/store/useDemoStore";
import { currentStepFor, nextStepFor, prevStepFor, FLOW } from "@/lib/flow";

export function NextStepHint() {
  const [path, setLocation] = useLocation();
  const setRole = useDemoStore((s) => s.setRole);
  const mode = useDemoStore((s) => s.settings.mode);
  const current = currentStepFor(path);
  const next = nextStepFor(path);
  const prev = prevStepFor(path);

  if (mode !== "demo-guiada") return null;
  if (!current || !next) return null;

  return (
    <AnimatePresence>
      <motion.div
        key={path}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 14 }}
        transition={{ duration: 0.25 }}
        style={{
          position: "fixed",
          bottom: 18,
          right: 18,
          zIndex: 50,
          background: "white",
          border: "1px solid var(--cc-gray-200)",
          borderRadius: 12,
          boxShadow: "0 12px 32px -8px rgba(15,23,42,0.18)",
          padding: 14,
          width: 340,
        }}
        data-testid="next-step-hint"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div
            style={{
              width: 26, height: 26, borderRadius: 999,
              background: "var(--cc-teal-bg)", color: "var(--cc-teal)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <MapPin size={13} />
          </div>
          <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            Paso {current.step} de {FLOW.length} · {current.label}
          </div>
        </div>
        <div style={{ fontSize: 13, color: "var(--cc-gray-700)", marginBottom: 12, lineHeight: 1.5 }}>
          Siguiente paso del flujo:&nbsp;<strong style={{ color: "var(--cc-gray-900)" }}>{next.label}</strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {prev && (
            <button
              type="button"
              data-testid="button-prev-step"
              onClick={() => {
                setRole(prev.role);
                setLocation(prev.path);
              }}
              title={`Regresar a: ${prev.label}`}
              aria-label={`Regresar al paso anterior: ${prev.label}`}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 34, height: 34, borderRadius: 8,
                background: "white", border: "1px solid var(--cc-gray-200)",
                color: "var(--cc-gray-700)", cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <ArrowLeft size={15} />
            </button>
          )}
          <Link
            href={next.path}
            data-testid="button-next-step"
            onClick={() => setRole(next.role)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 14px", borderRadius: 8,
              background: "linear-gradient(135deg, var(--cc-navy), var(--cc-teal))",
              color: "white", fontSize: 12, fontWeight: 600,
              textDecoration: "none", flex: 1, justifyContent: "center",
            }}
          >
            Continuar como {roleHomes[next.role] === "/admin" ? "administrador" : next.role}
            <ArrowRight size={14} />
          </Link>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
